import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { StatTile } from '~/components/ui/stat-tile';
import { StatusBadge } from '~/components/ui/status-badge';
import { LineChart } from '~/components/charts/line-chart';
import { GroupedBarChart } from '~/components/charts/bar-chart';
import { Sparkline } from '~/components/charts/sparkline';
import { categorical, compactNumber, formatDelta, formatFullNumber, formatPercentFraction } from '~/lib/viz';
import {
  channels2026,
  channelSubsStatus,
  channelYearly2025,
  channelYearly2026,
  productFunnel,
  topPosts,
  totalMonthly2025,
  totalMonthly2026,
  totalMonthlyCombined,
  yearTotals,
} from '~/data/social-stats';

/** 2026 закрыт по сентябрь: 9 из 12 месяцев. Используется везде, где показывается год к году. */
const MONTHS_ELAPSED_2026 = 9;
const MONTHS_TOTAL_YEAR = 12;

const BLUE = categorical[0];

const NEW_CHANNEL_META: Record<string, { purpose: string; launch: string }> = {
  'MAX Бренд': { purpose: 'Бренд-присутствие на новой площадке MAX', launch: 'янв 2026' },
  'MAX Продукт': { purpose: 'Продуктовая линейка на MAX, продажи', launch: 'апр 2026' },
  'ВК Продукт': { purpose: 'Продуктовый канал ВК, продажи', launch: 'янв 2026' },
  'TG NL Все о бизнесе': { purpose: 'Бизнес-возможность, рекрутинг партнёров', launch: 'янв 2026, репозиционирован в авг' },
  TikTok: { purpose: 'Тестовая продуктовая площадка, короткое видео', launch: 'янв 2026' },
};

function postsPair(label: string): [number, number] | null {
  const m = label.match(/(\d+)\s*→\s*(\d+)/);
  if (!m) return null;
  return [Number(m[1]), Number(m[2])];
}

const POSTS_OLD_CHANNELS: { channel: string; perMonth: [number, number] }[] = [
  { channel: 'ВК Бренд', perMonth: postsPair('64 → 73')! },
  { channel: 'TG NL news', perMonth: postsPair('92 → 97')! },
  { channel: 'Одноклассники', perMonth: postsPair('65 → 59')! },
  { channel: 'Instagram', perMonth: postsPair('30 → 31')! },
];

const POSTS_NEW_CHANNELS = [
  { channel: 'MAX Бренд', perMonth: 76 },
  { channel: 'MAX Продукт', perMonth: 54 },
  { channel: 'ВК Продукт', perMonth: 29 },
  { channel: 'TG NL Все о бизнесе', perMonth: 23 },
  { channel: 'Pinterest', perMonth: 21 },
  { channel: 'TikTok', perMonth: 11 },
];

function channelStatusFor(name: string) {
  return channelSubsStatus.find((c) => c.channel === name) ?? null;
}

export function SocialDashboardPage() {
  const combinedLabels = totalMonthlyCombined.map((p) => p.label);
  const yearBoundaryIndex = totalMonthly2025.length;

  const newChannels = channels2026.filter((c) => c.isNew);
  const oldChannels = channels2026.filter((c) => !c.isNew);

  const reachRunRate2026 = (yearTotals.y2026.reach / MONTHS_ELAPSED_2026) * MONTHS_TOTAL_YEAR;
  const reachRunRateYoy = reachRunRate2026 / yearTotals.y2025.reach - 1;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 sm:px-8">
      <div className="mx-auto max-w-6xl space-y-10">
        <header className="space-y-2">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Дашборд · соцсети NL</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Динамика 2025 → 2026</h1>
          <p className="max-w-3xl leading-relaxed text-slate-600">
            Охват, подписчики и вовлечённость по всем площадкам NL — в целом и по каждому каналу. За основу взята
            сводка «Дашборд, 2025-2026» из исходной таблицы.
          </p>
        </header>

        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <span className="mt-0.5 text-amber-600" aria-hidden>
            ⚠
          </span>
          <p className="text-sm leading-relaxed text-amber-900">
            <span className="font-semibold">2026 год не завершён.</span> Ниже везде — факт за{' '}
            <span className="font-semibold">9 месяцев (январь–сентябрь)</span>, против полных 12 месяцев 2025 года.
            Все годовые приросты на дашборде — промежуточные: итоговые цифры за 2026 год будут выше факта и появятся
            только после декабря.
          </p>
        </div>

        {/* KPI */}
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label="Охват, 2026 (янв–сент, факт)"
            value={compactNumber(yearTotals.y2026.reach)}
            delta={formatDelta(yearTotals.yoyPct.reach)}
            deltaPositive={yearTotals.yoyPct.reach > 0}
            hint="vs 2025, 12 мес. — сравнение неполного года с полным"
            secondary={`По темпу 9 мес. ≈ ${compactNumber(reachRunRate2026)} за весь год (${formatDelta(reachRunRateYoy)} г/г) — грубая оценка, не факт`}
          />
          <StatTile
            label="Подписчики, на конец сентября"
            value={compactNumber(yearTotals.y2026.subs)}
            delta={formatDelta(yearTotals.yoyPct.subs)}
            deltaPositive={yearTotals.yoyPct.subs > 0}
            hint={`+${formatFullNumber(yearTotals.yoyAbs.subs)} чел. г/г`}
            secondary="Ещё 3 месяца до конца года — база может подрасти"
          />
          <StatTile
            label="ER за год, среднее по площадкам"
            value={formatPercentFraction(yearTotals.y2026.er)}
            delta={formatDelta(yearTotals.yoyPct.er)}
            deltaPositive={yearTotals.yoyPct.er > 0}
            hint="2025: 5,7% — разбавлено новыми массовыми площадками"
          />
          <StatTile
            label="Постов в месяц"
            value="442"
            delta="+83%"
            deltaPositive
            hint="2025: 242 — объём почти удвоился"
            secondary="Среднее в месяц — к неполному году не чувствительно"
          />
        </section>

        {/* Основа: Дашборд, 2025-2026 */}
        <section className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">2025 → 2026 по каналам</h2>
            <p className="text-sm text-slate-500">
              Как в листе «Дашборд, 2025-2026» исходной таблицы: 2025 — полный год, 6 каналов; 2026 — янв–сент, 11
              каналов (5 из них запущены только в 2026-м).
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">2025 — полный год</CardTitle>
                <CardDescription>6 каналов</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full min-w-[420px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Канал</th>
                      <th className="py-2 pr-4">Охват</th>
                      <th className="py-2 pr-4">Подписчики</th>
                      <th className="py-2 pr-4">ER</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelYearly2025.map((row) => (
                      <tr key={row.channel} className="border-b border-slate-100">
                        <td className="py-2 pr-4 font-medium text-slate-900">{row.channel}</td>
                        <td className="py-2 pr-4 text-slate-600">{formatFullNumber(row.reach)}</td>
                        <td className="py-2 pr-4 text-slate-600">{formatFullNumber(row.subs)}</td>
                        <td className="py-2 pr-4 text-slate-600">{row.er == null ? '—' : formatPercentFraction(row.er)}</td>
                      </tr>
                    ))}
                    <tr className="font-semibold text-slate-900">
                      <td className="py-2 pr-4">ИТОГО</td>
                      <td className="py-2 pr-4">{formatFullNumber(yearTotals.y2025.reach)}</td>
                      <td className="py-2 pr-4">{formatFullNumber(yearTotals.y2025.subs)}</td>
                      <td className="py-2 pr-4">{formatPercentFraction(yearTotals.y2025.er)}</td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="text-base">2026 — янв–сент, год не завершён</CardTitle>
                <CardDescription>11 каналов, 9 из 12 месяцев</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full min-w-[420px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4">Канал</th>
                      <th className="py-2 pr-4">Охват</th>
                      <th className="py-2 pr-4">Подписчики</th>
                      <th className="py-2 pr-4">ER</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelYearly2026.map((row) => (
                      <tr key={row.channel} className="border-b border-slate-100">
                        <td className="py-2 pr-4 font-medium text-slate-900">{row.channel}</td>
                        <td className="py-2 pr-4 text-slate-600">{formatFullNumber(row.reach)}</td>
                        <td className="py-2 pr-4 text-slate-600">{formatFullNumber(row.subs)}</td>
                        <td className="py-2 pr-4 text-slate-600">{row.er == null ? '—' : formatPercentFraction(row.er)}</td>
                      </tr>
                    ))}
                    <tr className="font-semibold text-slate-900">
                      <td className="py-2 pr-4">ИТОГО (факт, 9 мес.)</td>
                      <td className="py-2 pr-4">{formatFullNumber(yearTotals.y2026.reach)}</td>
                      <td className="py-2 pr-4">{formatFullNumber(yearTotals.y2026.subs)}</td>
                      <td className="py-2 pr-4">{formatPercentFraction(yearTotals.y2026.er)}</td>
                    </tr>
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-600">
            <span className="font-medium text-slate-900">Прирост год к году (по факту, 2026 — неполный):</span> охват{' '}
            <span className="font-medium text-slate-900">{formatDelta(yearTotals.yoyPct.reach)}</span> (
            {formatFullNumber(yearTotals.yoyAbs.reach)}), подписчики{' '}
            <span className="font-medium text-slate-900">{formatDelta(yearTotals.yoyPct.subs)}</span> (
            {formatFullNumber(yearTotals.yoyAbs.subs)} чел.), ER{' '}
            <span className="font-medium text-slate-900">
              {yearTotals.yoyPct.er > 0 ? '+' : ''}
              {(yearTotals.yoyPct.er * 100).toFixed(1).replace('.', ',')} п.п.
            </span>
            .
          </div>
        </section>

        {/* Общая динамика */}
        <section className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Динамика в целом</h2>
            <p className="text-sm text-slate-500">
              Сумма по всем площадкам, помесячно, янв 2025 — сент 2026. Правая часть графика (2026) короче левой
              (2025) ровно на 3 месяца — год ещё не закончился.
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Суммарный охват</CardTitle>
              </CardHeader>
              <CardContent>
                <LineChart
                  labels={combinedLabels}
                  series={[{ key: 'reach', label: 'Охват', color: BLUE }]}
                  values={{ reach: totalMonthlyCombined.map((p) => p.reach) }}
                  formatValue={(v) => formatFullNumber(v)}
                  formatTick={(v) => compactNumber(v)}
                  areaFill
                  referenceIndex={yearBoundaryIndex}
                  referenceLabel="старт новых площадок"
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Суммарные подписчики</CardTitle>
              </CardHeader>
              <CardContent>
                <LineChart
                  labels={combinedLabels}
                  series={[{ key: 'subs', label: 'Подписчики', color: categorical[2] }]}
                  values={{ subs: totalMonthlyCombined.map((p) => p.subs) }}
                  formatValue={(v) => formatFullNumber(v)}
                  formatTick={(v) => compactNumber(v)}
                  areaFill
                  referenceIndex={yearBoundaryIndex}
                  referenceLabel="старт новых площадок"
                />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Новые площадки */}
        <section className="space-y-4 rounded-2xl border border-blue-200 bg-blue-50/40 p-5 sm:p-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Новые площадки — под продукт и бизнес</h2>
            <p className="text-sm text-slate-600">
              5 каналов запущены в 2026 году под конкретные задачи компании: продажи продукта и привлечение партнёров.
              Именно они дали почти весь прирост охвата и подписчиков за год.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {newChannels.map((c, i) => {
              const meta = NEW_CHANNEL_META[c.name];
              const st = channelStatusFor(c.name);
              return (
                <div key={c.name} className="rounded-xl border border-blue-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                    {st ? <StatusBadge kind={st.status} /> : null}
                  </div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">
                    {compactNumber(c.monthly[c.monthly.length - 1]?.subs ?? null)}
                  </div>
                  <div className="text-xs text-slate-500">подписчиков сейчас</div>
                  <div className="mt-2">
                    <Sparkline values={c.monthly.map((m) => m.subs)} color={categorical[i % categorical.length]} width={100} height={28} />
                  </div>
                  <div className="mt-2 text-xs leading-relaxed text-slate-500">{meta?.purpose}</div>
                  <div className="mt-1 text-xs text-slate-400">Старт: {meta?.launch}</div>
                </div>
              );
            })}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">MAX Продукт — бизнес-результат</CardTitle>
                <CardDescription>Визиты и доход по UTM-разметке, 2026</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-lg font-semibold text-slate-900">{formatFullNumber(productFunnel.maxProdukt.visits)}</div>
                  <div className="text-xs text-slate-500">визитов</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-slate-900">{compactNumber(productFunnel.maxProdukt.revenue)} ₽</div>
                  <div className="text-xs text-slate-500">доход</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-slate-900">{productFunnel.maxProdukt.cpc.toFixed(1)} ₽</div>
                  <div className="text-xs text-slate-500">цена визита</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">ВК Продукт — бизнес-результат</CardTitle>
                <CardDescription>Визиты и доход по UTM-разметке, 2026</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-3 gap-3">
                <div>
                  <div className="text-lg font-semibold text-slate-900">{formatFullNumber(productFunnel.vkProdukt.visits)}</div>
                  <div className="text-xs text-slate-500">визитов</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-slate-900">{compactNumber(productFunnel.vkProdukt.revenue)} ₽</div>
                  <div className="text-xs text-slate-500">доход</div>
                </div>
                <div>
                  <div className="text-lg font-semibold text-slate-900">{productFunnel.vkProdukt.cpc.toFixed(1)} ₽</div>
                  <div className="text-xs text-slate-500">цена визита</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* По отдельным площадкам */}
        <section className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">По отдельным площадкам</h2>
            <p className="text-sm text-slate-500">
              Подписчики помесячно, 2026. Статус — рост / держится / падение относительно средней базы янв–июл.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[...oldChannels, ...newChannels].map((c) => {
              const st = channelStatusFor(c.name);
              const last = c.monthly[c.monthly.length - 1];
              return (
                <Card key={c.name}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-400">{c.isNew ? 'новый канал 2026' : 'существовал в 2025'}</div>
                      </div>
                      {st ? <StatusBadge kind={st.status} /> : null}
                    </div>
                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <div className="text-lg font-semibold text-slate-900">{compactNumber(last?.subs ?? null)}</div>
                        <div className="text-xs text-slate-500">
                          {st ? `к базе: ${formatDelta(st.deltaToBase)}` : 'подписчиков'}
                        </div>
                      </div>
                      <Sparkline values={c.monthly.map((m) => m.subs)} color={BLUE} width={110} height={32} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Контент 2025 vs 2026 */}
        <section className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Контент: 2025 vs 2026</h2>
            <p className="text-sm text-slate-500">Постов в месяц, в среднем. Старые каналы — прямое сравнение период к периоду.</p>
          </div>
          <Card>
            <CardContent className="pt-6">
              <GroupedBarChart
                categories={POSTS_OLD_CHANNELS.map((c) => c.channel)}
                series={[
                  { key: '2025', label: '2025', color: categorical[1] },
                  { key: '2026', label: '2026', color: BLUE },
                ]}
                values={{
                  '2025': POSTS_OLD_CHANNELS.map((c) => c.perMonth[0]),
                  '2026': POSTS_OLD_CHANNELS.map((c) => c.perMonth[1]),
                }}
                formatValue={(v) => `${Math.round(v)} пост./мес`}
                formatTick={(v) => String(Math.round(v))}
              />
            </CardContent>
          </Card>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {POSTS_NEW_CHANNELS.map((c) => (
              <div key={c.channel} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                <div className="text-xs text-slate-500">{c.channel}</div>
                <div className="text-base font-semibold text-slate-900">{c.perMonth} пост./мес</div>
                <div className="text-xs text-slate-400">новый канал</div>
              </div>
            ))}
          </div>
        </section>

        {/* Лучшие моменты */}
        <section className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Самые выигрышные моменты</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <HighlightCard
              title="Топ-ролик года"
              body={`Reels «${topPosts[0].title}» (Instagram, ${topPosts[0].month}) — ${formatFullNumber(topPosts[0].views)} просмотров и ${formatFullNumber(topPosts[0].reactions)} реакций на один ролик.`}
            />
            <HighlightCard
              title="MAX Бренд — быстрый старт"
              body="С нуля до 26,5 тыс. подписчиков и 34,3 млн охвата за 9 месяцев — больше охвата, чем у ВК Бренд, который копил аудиторию годами."
            />
            <HighlightCard
              title="Вирусная волна в Instagram"
              body="Апрель–июнь: ERR до 654% — карусели и рилсы про бизнес-пакеты и истории участников разогнали вовлечение кратно выше обычного."
            />
            <HighlightCard title="ВК Бренд — стабильный рост" body="Охват +44% год к году при сопоставимом темпе публикаций — лучший результат среди устоявшихся каналов." />
            <HighlightCard
              title="MAX Продукт — самый дешёвый визит"
              body={`CPC ${productFunnel.maxProdukt.cpc.toFixed(0)} ₽ против ${productFunnel.vkProdukt.cpc.toFixed(0)} ₽ у ВК Продукт — ${compactNumber(productFunnel.maxProdukt.revenue)} ₽ дохода с ${formatFullNumber(productFunnel.maxProdukt.visits)} визитов.`}
            />
            <HighlightCard
              title="«Грант на мечту» после репозиционирования"
              body="С 2,9 тыс. до 7,7 тыс. подписчиков за сентябрь — рост почти в 2,7 раза за месяц после смены позиционирования в августе."
            />
          </div>
        </section>

        {/* Выводы */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-slate-900">Выводы</h2>
          <Card>
            <CardContent className="py-6">
              <ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed text-slate-700">
                <li>
                  <span className="font-medium text-slate-900">2026 год не завершён</span> — все цифры ниже за 9 из 12
                  месяцев. Сравнение с полным 2025 годом уже показывает уверенный рост, но итоговый годовой результат
                  будет выше факта: минимум ещё 3 месяца охвата, постов и, возможно, подписчиков добавятся после
                  декабря.
                </li>
                <li>
                  Рост охвата (+86%) и подписчиков (+12,3%) за неполный 2026 год обеспечен почти целиком запуском новых
                  площадок
                  (MAX, ВК Продукт, TG «Всё о бизнесе», TikTok). Аудитория старых каналов за 9 месяцев сократилась
                  примерно на 2,6% — без новых каналов общий результат был бы в минусе.
                </li>
                <li>
                  Среди «старых» каналов ВК — лучший performer: охват +44% г/г. Telegram, напротив, теряет охват (−35%)
                  и подписчиков — стоит разобрать причины по колонке «Что делали / гипотеза» за проблемные месяцы.
                </li>
                <li>
                  Объём контента вырос в 1,8 раза (242 → 442 постов/мес), но отдача с одного поста упала практически
                  везде: в ВК на 24%, в Telegram на 33%, в Instagram на 16%. Рост количества обгоняет рост внимания
                  аудитории — стоит пересмотреть баланс в пользу качества.
                </li>
                <li>
                  Продуктовые каналы уже дают измеримый бизнес-результат: совокупно ≈1,43 млн ₽ дохода по UTM. Но цена
                  визита в ВК Продукт в 6 раз выше, чем в MAX Продукт (247 ₽ vs 38 ₽) — бюджет логично смещать в сторону
                  MAX.
                </li>
                <li>
                  Бизнес-канал «Грант на мечту» после репозиционирования в августе показал кратный рост — гипотеза
                  подтвердилась, стоит дать каналу ресурс на удержание темпа.
                </li>
                <li>
                  Средний ER по всем площадкам упал с 5,7% до 2,1% — это не деградация контента, а эффект разбавления:
                  новые массовые каналы (MAX, Pinterest) дают огромный охват при низком ER по своей природе. Смотреть
                  VR/ERR по каждому каналу отдельно, а не сводить в одну общую цифру.
                </li>
              </ol>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}

function HighlightCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="mt-1 text-sm leading-relaxed text-slate-600">{body}</div>
    </div>
  );
}
